import { lighten, makeStyles, Theme } from '@material-ui/core/styles';
import { LoginLogoImg } from '@roc/feature-app-core';
import { PrintableArea } from '@roc/ui';

import { observer } from 'mobx-react';
import { forwardRef, useEffect, useState } from 'react';
import {
  formatCurrency,
  formatPercentage,
  getCompletePropertyAddress,
} from '@roc/ui/utils';
import { Box, Divider, Grid, Typography } from '@material-ui/core';
import {
  DateFormat,
  DUPLEX,
  formatDate,
  getDaysBetween,
  insertIf,
  isEmptyString,
  LoanSubType,
  parseDate,
  PURCHASE,
  QUADRUPLEX,
  REFINANCE,
  SINGLE_FAMILY,
  TRIPLEX,
} from '@roc/feature-utils';
import { visibility } from 'html2canvas/dist/types/css/property-descriptors/visibility';
import { useStore } from '@roc/client-portal-shared/hooks';
import { PropertyType } from '@roc/ui/formComponents';
import { AnnualHOA } from 'libs/client-portal-shared/src/app/modules/dscrCalculator/components/dscrCalculatorFields';

const useStyles = makeStyles((theme: Theme) => ({
  page: {
    color: 'black',
    fontSize: '0.12in',

    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'verdana, arial, sans-serif',
    lineHeight: '1',
    position: 'relative',

    ['& p']: {
      margin: '1em 0',
    },
  },

  title: {
    fontSize: '0.40in',
    paddingLeft: '16px',
    fontFamily: '"Times New Roman", Times, serif',
    letterSpacing: '0.005in',
    color: theme.palette.primary.dark,
  },

  light: {
    color: 'rgba(89, 89, 89, 1)',
    fontFamily: '"Times New Roman", Times, serif',
    letterSpacing: '0.004in',
  },

  loanEconomicsValue: {
    color: 'black',
    paddingTop: '4px',
    fontSize: '0.2in',
    fontWeight: 'bold',
  },

  sectionHeader: {
    fontSize: '0.18in',
    height: '26px',
    borderBottom: '1px solid rgba(101, 101, 101, 1)',
    color: 'rgba(101, 101, 101, 1)',
    marginBottom: '10px',
    paddingLeft: '8px',
    marginTop: theme.spacing(2),
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },

  thickRow: {
    borderTop: '2pt solid ' + lighten(theme.palette.primary.dark, 0.3),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    ['& img']: {
      padding: '0',
    },
  },
  headerDate: {
    color: theme.palette.primary.dark,
  },
  logoWrapper: {
    paddingRight: '8px',
  },
  logoContainer: {
    margin: 0,
  },
  footer: {
    paddingTop: '30px',
    fontSize: '0.08in',
    color: 'black',
  },
  footerRates: {
    display: 'flex',
    fontSize: '0.10in',
    ['& div:nth-child(1)']: {
      width: '45%',
    },
    ['& div:nth-child(2)']: {
      width: '30%',
    },
    ['& div:nth-child(3)']: {
      width: '25%',
    },
  },
  footerNote: {
    color: 'rgba(0, 0, 0, 0.38)',
  },

  bold: {
    fontWeight: 'bold',
  },
  sectionTitle: {
    paddingTop: '6px',
    paddingBottom: '6px',
  },
  darkBackground: {
    backgroundColor: theme.palette.primary.light,
    fontWeight: 'bold',
    textAlign: 'right',
    color: 'black',
  },

  section: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    padding: '4pt 0',
  },

  valuesTable: {
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',

    ['& td ']: {
      height: '22px',
    },
    ['& tr td:nth-child(odd)']: {
      color: '#595959',
      paddingLeft: theme.spacing(5),
      fontFamily: '"Times New Roman", Times, serif',
      letterSpacing: '0.004in',
    },
    ['& tr td:nth-child(even)']: {
      color: theme.palette.primary.dark,
    },
  },

  pricingOptionsTable: {
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',

    ['& td ']: {
      fontWeight: 'bold',
      height: '27px',
      textAlign: 'center',
      color: theme.palette.primary.dark,
      borderLeft: '1px solid rgba(89, 89, 89, 1)',
    },

    ['& td:first-child']: {
      fontWeight: 'normal',
      paddingLeft: theme.spacing(5),
      textAlign: 'left',
      color: 'black',
      borderLeft: 'none',
      fontFamily: '"Times New Roman", Times, serif',
      letterSpacing: '0.004in',
    },

    ['& tr:nth-child(even) td']: {
      backgroundColor: 'rgba(247, 249, 238, 1)',
    },

    ['& tr:first-child td']: {
      color: 'rgba(89, 89, 89, 1)',
      fontWeight: 'normal',
      borderBottom: '1px solid rgba(89, 89, 89, 1)',
    },
  },

  closingCostsTableLine: {
    ['& td']: {
      borderBottom: '1px solid #595959',
    },
    ['& td:nth-child(1)']: {
      borderBottom: 0,
    },
  },

  showOnPrint: {
    display: 'none',
  },
  hideOnPrint: {
    display: 'block',
  },
  print: {
    //A4 size page
    width: '8.27in',
    height: '11.69in',

    //Add paddings
    padding: '0 0.65in',
    ['& $header']: {
      paddingTop: '0.6in',
    },

    ['& $footer']: {
      paddingBottom: '0.6in',
    },

    //Display hidden elements
    ['& $showOnPrint']: {
      display: 'block',
    },
    ['& $hideOnPrint']: {
      display: 'none',
    },
  },
}));

interface Props {
  data: any;
}

export const DownloadableLoanRatesHtml = observer(
  forwardRef<any, any>(({ data }: Props, ref) => {
    const classes = useStyles();
    return (
      <PrintableArea printClass={classes.print} ref={ref}>
        <DownloadableQuoteComponent data={data} />
      </PrintableArea>
    );
  })
);

const DownloadableQuoteComponent = observer(({ data }: Props) => {
  const classes = useStyles();
  const properties: any[] = data.properties;

  const borrowersCount = data?.borrowers?.length || 0;
  const splitInPages = borrowersCount > 3 || !!data.brokerName;

  return (
    <>
      <div className={classes.page}>
        <Header data={data} />
        <Content>
          <SectionHeader>BORROWER INFORMATION</SectionHeader>
          <BorrowerInformation data={data} />
          <SectionHeader>PROPERTY INFORMATION</SectionHeader>
          <PropertiesInformation data={data} />
          <SectionHeader>COMPARE PRICING OPTIONS</SectionHeader>
          <PricingOptions data={data} />
        </Content>
        <Footer data={data} />
      </div>
    </>
  );
});

const BorrowerInformation = ({ data }: Props) => {
  const classes = useStyles();
  const borrowers = data.borrowers;
  const entityName = data.entity?.entityName;

  return (
    <table className={classes.valuesTable}>
      {borrowers.map(borrower => (
        <>
          {borrowers.length > 0 && (
            <tr>
              <td colSpan={1}>Name</td>
              <td colSpan={3}>
                {borrower.firstName + ' ' + borrower.lastName}
              </td>
            </tr>
          )}
          <tr>
            <td>Estimated FICO Score</td>
            <td>{borrower.ficoScore}</td>
            <td>Verified Investment Properties</td>
            <td>{borrower.experience}</td>
          </tr>
        </>
      ))}
      {!!entityName && (
        <tr>
          <td>Entity Name</td>
          <td>{entityName}</td>
        </tr>
      )}
    </table>
  );
};

const PropertiesInformation = ({ data }: Props) => {
  const classes = useStyles();
  const properties: any[] = data.properties;

  return (
    <>
      {properties.map((property, i) => (
        <PropertyInformation property={property} data={data} />
      ))}
    </>
  );
};

const PropertyInformation = ({ property, data }: Props & { property: any }) => {
  const classes = useStyles();

  const isRefinance = REFINANCE === property.loanPurpose;
  const isPurchase = PURCHASE === property.loanPurpose;

  const isFixFlip = data.loanSubtype === LoanSubType.FIX_AND_FLIP;
  const isStabilizedBridge = data.loanSubtype === LoanSubType.STABILIZED_BRIDGE;
  const isGroundUp = data.loanSubtype === LoanSubType.GROUND_UP;
  const isSingleProperty = data.loanSubtype === LoanSubType.SINGLE_PROPERTY;

  const propertyAddress = {
    label: 'Property Address',
    value: property.address,
    columns: 2,
  };

  const emptySpace = { break: true, label: '', value: '' };

  const purchaseDate = {
    label: 'Purchase Date',
    value: property.purchaseDate,
  };

  const propertyType = {
    label: 'Property Type',
    value: property.propertyType,
  };

  const purchasePrice = {
    label: 'Purchase Price',
    value: property.purchasePrice,
  };

  const numberOfUnits = {
    label: 'Number of Units',
    value: {
      [SINGLE_FAMILY]: 1,
      [DUPLEX]: 2,
      [TRIPLEX]: 3,
      [QUADRUPLEX]: 4,
    }[property.propertyType],
  };

  const totalRenovationBudget = {
    label: 'Total Renovation Budget',
    value: property.renovationBudget,
  };

  const estimatedAsIsValue = {
    label: isStabilizedBridge
      ? 'Estimated As-Is Value*'
      : 'Estimated Site Value / As-Is Value',
    value: property.estimatedAsIsValue,
  };

  const releaseAmount = {
    label: 'Release Amount',
    value: property.releaseAmount,
  };

  const propertyLoanAllocatedAmount = {
    label: 'Property Loan Allocated Amount',
    value: property.propertyLoanAllocatedAmount,
  };

  const completedRenoToDate = {
    label: 'Completed Reno to Date',
    value: property.completedRenoToDate,
  };

  const estimatedPayoffAmount = {
    label: 'Estimated Payoff Amount',
    value: property.estimatedPayoffAmount,
  };

  const estimatedARV = {
    label: 'Estimated ARV',
    value: property.marketValueAfterCompletion,
  };

  const monthlyMarketRent = {
    label: 'Monthly Market Rent',
    value: property.monthlyMarketRent,
  };

  const annualTaxes = {
    label: 'Annual Taxes',
    value: property.annualTaxes,
  };

  const annualInsurance = {
    label: 'Annual Insurance',
    value: property.annualInsurance,
  };

  const permitsInPlace = {
    label: 'Permits',
    value: property.permitsInPlace ? 'Yes' : 'No',
  };

  const annualHOA = {
    label: 'Annual HOA',
    value: property.annualHOA,
  };

  const fixFlipTable = [
    propertyAddress,
    ...insertIf(isPurchase, [
      purchasePrice,
      propertyType,
      totalRenovationBudget,
      numberOfUnits,
      emptySpace,
      estimatedARV,
    ]),
    ...insertIf(isRefinance, [
      purchaseDate,
      propertyType,
      purchasePrice,
      numberOfUnits,
      completedRenoToDate,
      estimatedAsIsValue,
      totalRenovationBudget,
      estimatedARV,
      emptySpace,
      estimatedPayoffAmount,
    ]),
  ];

  const groundUpTable = [
    propertyAddress,
    ...insertIf(isPurchase, [
      purchasePrice,
      propertyType,
      totalRenovationBudget,
      numberOfUnits,
      permitsInPlace,
      estimatedAsIsValue,
      emptySpace,
      estimatedARV,
    ]),
    ...insertIf(isRefinance, [
      purchaseDate,
      propertyType,
      purchasePrice,
      numberOfUnits,
      completedRenoToDate,
      estimatedAsIsValue,
      totalRenovationBudget,
      estimatedARV,
      permitsInPlace,
      estimatedPayoffAmount,
    ]),
  ];

  const stabilizedBridgeTable = [
    propertyAddress,
    ...insertIf(isPurchase, [
      purchasePrice,
      propertyType,
      monthlyMarketRent,
      numberOfUnits,
      emptySpace,
      estimatedAsIsValue,
      emptySpace,
      annualTaxes,
      emptySpace,
      annualInsurance,
    ]),
    ...insertIf(isRefinance, [
      purchaseDate,
      propertyType,
      purchasePrice,
      numberOfUnits,
      completedRenoToDate,
      estimatedAsIsValue,
      monthlyMarketRent,
      annualTaxes,
      emptySpace,
      annualInsurance,
    ]),
  ];

  const singlePropertyTable = [
    propertyAddress,
    ...insertIf(isPurchase, [
      purchasePrice,
      propertyType,
      monthlyMarketRent,
      numberOfUnits,
      estimatedAsIsValue,
      annualTaxes,
      annualHOA,
      annualInsurance,
    ]),
    ...insertIf(isRefinance, [
      purchasePrice,
      propertyType,
      purchaseDate,
      numberOfUnits,
      estimatedPayoffAmount,
      estimatedAsIsValue,
      completedRenoToDate,
      annualTaxes,
      annualInsurance,
      monthlyMarketRent,
      annualHOA,
    ]),
  ];

  return (
    <table className={classes.valuesTable} key={property.address}>
      {isFixFlip && <TableBuilder cells={fixFlipTable} columns={2} />}
      {isGroundUp && <TableBuilder cells={groundUpTable} columns={2} />}
      {isStabilizedBridge && (
        <TableBuilder cells={stabilizedBridgeTable} columns={2} />
      )}
      {isSingleProperty && (
        <TableBuilder cells={singlePropertyTable} columns={2} />
      )}
    </table>
  );
};

const PricingOptions = ({ data }: Props) => {
  const classes = useStyles();
  const options = data.pricingOptions;
  const ONE_QUOTE = 1;
  const fields = [
    {
      label: 'Loan Purpose',
      values: options.map(option => option.loanPurpose),
    },
    {
      label: 'LTV',
      values: options.map(option => option.loanToValue),
    },
    {
      label: 'Loan Amount',
      values: options.map(option => option.loanAmount),
    },
    {
      label: 'Amortization',
      values: options.map(option => option.amortization),
    },
    {
      label: 'Pre-Payment Penalty',
      values: options.map(option => option.prePaymentPenalty),
    },
    {
      label: 'Rate Type',
      values: options.map(option => option.rateType),
    },
    {
      label: 'Index',
      values: options.map(option => option.index),
    },
    {
      label: 'Today’s Gross Rate',
      values: options.map(option => option.todayGrossRate),
    },
    {
      label: 'Today’s DSCR',
      values: options.map(option => option.todayDSCR),
    },
    {
      label: 'Monthly PI Payment',
      values: options.map(option => option.monthlyPIPayment),
    },
    {
      label: 'Monthly PITIA Payment',
      values: options.map(option => option.monthlyPITIAPayment),
    },
    {
      label: 'Post Loan Approval Rate Lock',
      values: options.map(option => option.postLoanApprovalRateLock),
    },
    {
      label: 'Corelogix Fee',
      values: options.map(option => option.corelogixFee),
    },
    {
      label: 'Origination Points',
      values: options.map(option => option.pointsAndFeesPercent),
    },
    {
      label: 'Admin Fee',
      values: options.map(option => option.adminFee),
    },
    {
      label: 'Legal Fee',
      values: options.map(option => option.legalFee),
    },
    {
      label: 'Commitment Fee',
      values: options.map(option => option.commitmentFee),
    },
    {
      label: 'Processing Fee',
      values: options.map(option => option.processingFee),
    },
    {
      label: 'Underwriting Fee',
      values: options.map(option => option.underwritingFee),
    },
    {
      label: 'Buydown Fee',
      values: options.map(option => option.buydownFee),
    },
  ];

  return (
    <table className={classes.pricingOptionsTable}>
      <tr>
        <td></td>
        {options.length > ONE_QUOTE &&
          options.map((opt, i) => <td key={i}>{'Quote ' + (i + 1)}</td>)}
        {options.length === ONE_QUOTE && <td key={ONE_QUOTE}>{'Quote'}</td>}
      </tr>
      {fields.map(field => (
        <tr key={field.label}>
          <td>{field.label}</td>
          {field.values.map((value, i) => (
            <td key={i}>{value}</td>
          ))}
        </tr>
      ))}
    </table>
  );
};

const SectionHeader = ({ children }) => {
  const classes = useStyles();
  return <Box className={classes.sectionHeader}>{children}</Box>;
};

const Header = ({ className = '', data }) => {
  const classes = useStyles();

  return (
    <div className={`${classes.header} ${className}`}>
      <div className={classes.logoWrapper}>
        <LoginLogoImg
          containerClassName={classes.logoContainer}
          imgStyle={{ height: '85px' }}
        />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex' }}>
          <div className={classes.title} style={{ flex: 1 }}>
            Single Property Rental
          </div>
          <Box>
            <Box pb={1} className={classes.light}>
              Date
            </Box>
            <div className={classes.headerDate} style={{ fontWeight: 'bold' }}>
              {formatDate(new Date(), DateFormat.MMDDYYYY)}
            </div>
          </Box>
        </div>

        {data.leverageException && (
          <Box pb={2}>
            <Typography
              style={{
                fontStyle: 'italic',
                fontSize: '16px',
                color: '#595959',
                margin: 0,
              }}
            >
              This quote is not pre approved and is subject to underwriting
              discretion
            </Typography>
          </Box>
        )}
      </div>
    </div>
  );
};

const Footer = ({ data, className = '' }) => {
  const classes = useStyles();
  return (
    <div className={`${classes.footer} ${className}`}>
      <b>Index and Rate Lock Information: </b>
      <p className={classes.footerRates}>
        <div>
          USD 3M Libor US Treasury Rates as of <b>02 / 04 / 2025</b>
        </div>
        <div>
          5 Yr US Treasury: <b>{data.fiveYrTsrySwap}</b>
        </div>
        <div>
          10 Yr US Treasury: <b>{data.tenYrTsrySwap}</b>
        </div>
      </p>
      <p className={classes.footerNote}>
        Please note this letter of interest serves to outline the terms of the
        proposed financing of the referenced transaction.This letter is merely a
        general proposal, and is neither a binding offer, nor a contract.
        Borrower understands that no such offer will be forthcoming prior to
        completion of appropriate due diligence and underwriting performed
        and/or contracted by Lender. This proposal does not create any legally
        binding obligations on any party hereto. All properties are subject to
        satisfactory Lender due diligence underwriting including: satisfactory
        appraisal, satisfactory credit review of the borrowing entity and Key
        Principals, and satisfactory review of the property’s market and
        submarket.
      </p>
      <p className={classes.footerNote}>
        Gross rates are determined at the time of credit approval.
      </p>
    </div>
  );
};

const Content = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.content}>{children}</div>;
};

const TableBuilder = ({ cells, columns }) => {
  const rows = cells.reduce(
    (rows, cell) => {
      let currentRow = rows[rows.length - 1];
      const colsInCurrentRow = currentRow.reduce(
        (total, c) => (c.columns || 1) + total,
        0
      );
      const doesNotFit = colsInCurrentRow + (cell.columns || 1) > columns;
      if (cell.break || doesNotFit) {
        currentRow = [];
        rows.push(currentRow);
      }
      currentRow.push(cell);
      return rows;
    },
    [[]]
  );

  return rows.map(cells => (
    <tr>
      {cells.map(cell => (
        <>
          <td>{cell.label}</td>
          <td colSpan={cell.columns * 2 - 1}>{cell.value}</td>
        </>
      ))}
    </tr>
  ));
};
